import React, { useState } from 'react';
import { Box, Stack } from '@mui/material';
import { BaseActivityMessage } from '../BaseActivityMessage';
import { Rating } from '../Rating';
import { RatingLabelContainer } from '../Rating/RatingLabelContainer';

export interface RatingActivityMessageProps {
  /**
   * Current rating value (1-5)
   */
  value: number;
  /**
   * Callback when rating changes
   */
  onChange: (value: number) => void;
  /**
   * Callback when submit button is clicked
   */
  onSubmit: () => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Whether the rating is disabled
   */
  disabled?: boolean;
  /**
   * Whether the rating is read-only (already submitted)
   */
  readOnly?: boolean;
  /**
   * Hide the submit button (used for locked / read-only rendering).
   */
  hideSubmit?: boolean;
  /**
   * Custom labels for each rating value
   */
  labels?: { [index: string]: string };
  /**
   * Size of the rating stars
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Name attribute for the rating component
   */
  name?: string;
  /**
   * Message content to display above the rating
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
  /**
   * Custom bot display name
   */
  botName?: string;
  /**
   * Optional content rendered below the prompt bubble (e.g. an AI-translation disclosure hint).
   */
  footer?: React.ReactNode;
}

/**
 * RatingActivityMessage displays a star rating interface with submit and optional skip buttons.
 * This component is designed for collecting user ratings in chat-like interfaces,
 * with support for custom labels and localization.
 */
export const RatingActivityMessage: React.FC<RatingActivityMessageProps> = ({
  value,
  onChange,
  onSubmit,
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Send',
  onSkip,
  disabled = false,
  readOnly = false,
  hideSubmit = false,
  labels = {
    1: 'Very Poor',
    2: 'Poor',
    3: 'Average',
    4: 'Good',
    5: 'Excellent',
  },
  size = 'large',
  name = 'feedback-rating',
  children,
  sender,
  avatarUrl,
  timestamp,
  botName,
  footer,
}) => {
  const [hover, setHover] = useState(0);

  return (
    <BaseActivityMessage
      allowSkip={allowSkip}
      skipText={skipText}
      submitText={submitText}
      onSkip={onSkip}
      disabled={disabled}
      submitEnabled={value >= 1}
      hideSubmit={hideSubmit}
      sender={sender}
      avatarUrl={avatarUrl}
      timestamp={timestamp}
      botName={botName}
      footer={footer}
      onSubmit={onSubmit}
      renderInput={() => (
        <Stack spacing={2} alignItems="center">
          <Rating
            name={name}
            value={value}
            precision={1}
            onChange={(_event, newValue) => newValue && onChange(newValue)}
            onChangeActive={(_event, newValue) => (newValue ? setHover(newValue) : setHover(0))}
            size={size}
            readOnly={readOnly}
            disabled={disabled}
            isLabelHidden={true}
          />
          <Box sx={{ minHeight: '40px' }}>
            <RatingLabelContainer labels={labels} hover={value || hover} size={size} />
          </Box>
        </Stack>
      )}
    >
      {children}
    </BaseActivityMessage>
  );
};
